import * as Sentry from "@sentry/react";
import { cp } from "./config";
import { discardUnsafeReplaySession, guardReplay } from "./utils/sentryReplayGuard";
import {
    scrubBreadcrumb,
    scrubErrorEvent,
    scrubRecordingEvent,
    scrubReplayEvent,
    scrubSpan,
    scrubTransaction,
} from "./utils/scrubUrl";
import { getSentrySessionId } from "./utils/sentrySessionId";

export const initSentry = () => {
    const environment = cp.cfg?.ENVIRONMENT ?? "unknown";
    const dsn = cp.cfg?.SENTRY_DSN;
    const isProd = environment === "prod";
    const isLocalhost = environment === "localhost";

    discardUnsafeReplaySession();

    Sentry.init({
        dsn,
        environment,
        release: process.env.REACT_APP_VERSION,
        sendDefaultPii: false,
        enabled: !!dsn && environment !== "unknown" && !isLocalhost,
        attachStacktrace: true,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: true,
                // SDK default, set explicitly: the privacy policy promises input masking, so it must not hinge on a default.
                maskAllInputs: true,
                blockAllMedia: true,
                // guardReplay stops and restarts the recording; see there why this must be 0.
                minReplayDuration: 0,
                beforeAddRecordingEvent: scrubRecordingEvent,
            }),
        ],
        tracesSampleRate: isProd ? 0.1 : 1.0,
        // Exclude lernen.phase-6.de (server.integration): its CORS config does not
        // allow the `sentry-trace`/`baggage` headers, so propagating to it triggers
        // a blocked preflight on cross-origin requests.
        tracePropagationTargets: [/^https:\/\/(?!lernen\.)([\w-]+\.)*phase-6\.(de|com)/],
        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 1.0,
        beforeBreadcrumb: scrubBreadcrumb,
        beforeSend: scrubErrorEvent,
        beforeSendTransaction: scrubTransaction,
        beforeSendSpan: scrubSpan,
    });

    Sentry.addEventProcessor(scrubReplayEvent);
    guardReplay();

    // sendDefaultPii is off, so without an id Sentry's "affected users" count stays at 0.
    Sentry.setUser({ id: getSentrySessionId() });
};

/**
 * Writes to the isolation scope, where `Sentry.setTag` writes since SDK v8.
 * @param role undefined removes the tag (logout); the diagnostic user id stays.
 */
export const setSentryRole = (role: string | undefined) => {
    Sentry.getIsolationScope().setTag("role", role);
};
